
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { getCurrencySymbol } from '@/lib/utils';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  setCurrency: (currency: string) => void;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider = ({ children }: CurrencyProviderProps) => {
  const { user } = useAuth();
  const [currency, setCurrencyState] = useState<string>('USD');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserCurrency = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('default_currency')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user currency:', error);
        } else if (data?.default_currency) {
          setCurrencyState(data.default_currency);
        }
      } catch (error) {
        console.error('Error fetching user currency:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserCurrency();
  }, [user]);

  const setCurrency = async (newCurrency: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ default_currency: newCurrency })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating currency:', error);
      } else {
        setCurrencyState(newCurrency);
      }
    } catch (error) {
      console.error('Error updating currency:', error);
    }
  };

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <CurrencyContext.Provider value={{ currency, currencySymbol, setCurrency, isLoading }}>
      {children}
    </CurrencyContext.Provider>
  );
};
